import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import { useSelector, useDispatch } from "react-redux";
import { RootState, RootDispatch } from "../../store";
import { IServiceTicket } from "../../models/serviceManagement.model";
import {
  getServiceStatuses,
  getServiceTicketsByStatus,
  reorderTicketsInColumn,
  updateServiceTicketStatus,
  updateTicketStatusLocally,
} from "../../store/slices/serviceManagementSlice";
import ServiceKanbanColumn from "./ServiceKanbanColumn";
import toast from "react-hot-toast";
import ServiceKanbanCard from "./ServiceKanbanCard";
import Loading from "../AssetsComponent/loading";

const ServiceKanbanBoard: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const statuses = useSelector(
    (state: RootState) => state.serviceManagement.serviceStatuses
  );
  const statusTickets = useSelector(
    (state: RootState) => state.serviceManagement.statusTickets
  );

  const fetchServiceStatusesLoading = useSelector(
    (state: RootState) =>
      state.serviceManagement.loadingState.fetchServiceStatusesLoading
  );

  const sensors = useSensors(useSensor(PointerSensor));

  const [activeId, setActiveId] = useState<string | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);
  const [activeTicket, setActiveTicket] = useState<IServiceTicket | null>(null);

  useEffect(() => {
    dispatch(getServiceStatuses());
  }, [dispatch]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setActiveTicket(event.active.data.current?.ticket);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverColumnId(event.over?.data.current?.columnId || null);
  };

  const onTicketDragEnd = async ({
    ticketId,
    sourceStatus,
    destinationStatus,
    currentIndex,
    destinationIndex,
  }: {
    ticketId: string;
    sourceStatus: string;
    destinationStatus: string;
    currentIndex: number;
    destinationIndex: number;
  }) => {
    try {
      await dispatch(
        updateServiceTicketStatus({
          ticketId,
          currentStatus: sourceStatus,
          destinationStatus,
          currentIndex,
          destinationIndex,
        })
      ).unwrap();

      if (sourceStatus !== destinationStatus) {
        toast.success(
          `Ticket moved from "${sourceStatus}" to "${destinationStatus}"`
        );
         if (destinationStatus === "resolved") {
           dispatch(getServiceTicketsByStatus({ status: "resolved", page: 1 }));
         }
      }
    } catch (err) {
      toast.error("Error updating ticket status");
      throw err;
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverColumnId(null);
    setActiveTicket(null);

    if (!over || !active?.data?.current || !over?.data?.current) return;

    const ticketId = active.id as string;
    const sourceStatus = active.data.current.status;
    const destStatus = over.data.current.status;

    if (!sourceStatus || !destStatus) return;

    const sourceColumn = statusTickets[sourceStatus];
    const destColumn = statusTickets[destStatus];
    const draggedTicket = sourceColumn?.tickets.find(
      (t: IServiceTicket) => t._id === ticketId
    );

    if (!draggedTicket) return;

    const overTicketId = over.id as string;
    let destinationIndex = -1;

    if (destColumn && overTicketId !== destStatus) {
      destinationIndex = destColumn.tickets.findIndex(
        (t: IServiceTicket) => t._id === overTicketId
      );
    }

    const currentIndex = sourceColumn.tickets.findIndex(
      (t: IServiceTicket) => t._id === ticketId
    );

    if (sourceStatus === destStatus) {
      // Reorder within same column
      const currentIndex = sourceColumn.tickets.findIndex(
        (t: IServiceTicket) => t._id === ticketId
      );
      if (currentIndex !== destinationIndex && destinationIndex >= 0) {
        dispatch(
          reorderTicketsInColumn({
            status: sourceStatus,
            dragIndex: currentIndex,
            hoverIndex: destinationIndex,
          })
        );
        await onTicketDragEnd({
          ticketId,
          sourceStatus,
          destinationStatus: destStatus,
          currentIndex,
          destinationIndex,
        });
      }
    } else {
      // Move to different column
      dispatch(
        updateTicketStatusLocally({
          ticketId,
          newStatus: destStatus,
          sourceStatus,
          destinationIndex:
            destinationIndex >= 0 ? destinationIndex : undefined,
        })
      );
      await onTicketDragEnd({
        ticketId,
        sourceStatus,
        destinationStatus: destStatus,
        currentIndex,
        destinationIndex: destinationIndex >= 0 ? destinationIndex : 0,
      });
    }
  };

  const handleLoadMore = (status: string) => {
    const column = statusTickets[status];
    if (!column || column.loading) return;

    const nextPage = column.page + 1;
    dispatch(
      getServiceTicketsByStatus({
        status,
        page: nextPage,
        limit: column.limit,
      })
    );
  };

  if (fetchServiceStatusesLoading) {
    return <Loading />;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <DragOverlay>
        {activeTicket ? (
          <div className="pointer-events-none opacity-80 transform rotate-5">
            <ServiceKanbanCard
              ticket={activeTicket}
              status={activeTicket.status!}
            />
          </div>
        ) : null}
      </DragOverlay>

      <div className="p-4">
        <div className="flex gap-6 overflow-x-auto pb-4">
          {statuses && statuses.length > 0 ? (
            statuses.map((status) => {
              const key = status.status;
              const column = statusTickets?.[key] ?? {
                tickets: [],
                loading: false,
                hasMore: false,
                totalCount: 0,
                page: 1,
                limit: 20,
              };

              return (
                <ServiceKanbanColumn
                  key={status._id}
                  status={status}
                  tickets={column.tickets}
                  loading={column.loading}
                  hasMore={column.hasMore}
                  totalCount={column.totalCount}
                  overColumnId={overColumnId}
                  onLoadMore={() => handleLoadMore(key)}
                />
              );
            })
          ) : (
            <div className="text-sm text-gray-500">No status columns found</div>
          )}
        </div>
      </div>
    </DndContext>
  );
};

export default ServiceKanbanBoard;
